import {useEffect, useMemo, useState} from 'react'
import {getActionFlowContextMappedSamples} from '../../../common/action-flows/config/helpers'
import {makeSampleUserProfile} from '../../../common/sample-formats/end-user'
import {
  ActionFlowConfig,
  AugmentedFlowContext
} from '../../../common/types/action-flows'
import {omit} from '../../../common/types/helpers'
import {useCustomProfileFields} from '../../../hooks/use-custom-profile-fields'

export const useActionFlowSampleContext = ({
  environmentId,
  config,
  stepId
}: {
  environmentId: string
  config: ActionFlowConfig
  stepId?: string
}): [AugmentedFlowContext | undefined, {hasLegacyStepMapping: boolean}] => {
  const [predictedContext, setPredictedContext] = useState<
    AugmentedFlowContext | undefined
  >()

  const [customProfileFields] = useCustomProfileFields(environmentId)

  // Grab custom fields and present them as properties of the users profile
  // getActionFlowContextMappedSamples will extend with core profile fields
  const profileCustomFields = useMemo(
    () =>
      customProfileFields.reduce(
        (acc, {name}) => ({
          ...acc,
          [name]: `<${name}>`
        }),
        {}
      ),
    [customProfileFields]
  )

  const step = Object.values(config.steps).find(({id}) => id === stepId)

  // Some old Mappings reference steps like so `steps["xyz"].value` vs the new format of `context.flowSteps["xyz"].value`
  // We only want to include steps in this part of the context if the step has a legacy mapping
  const hasLegacyStepMapping = useMemo(() => {
    if (
      step &&
      'properties' in step &&
      /"path":\s*"steps\[/.test(JSON.stringify(step.properties))
      // 😬 yeah kinda nasty quick search for the old shape, I don't think its worth the TS appeasing required to do this "properly"
    ) {
      return true
    }
    return false
  }, [step])

  useEffect(() => {
    getActionFlowContextMappedSamples(
      config,
      stepId,
      makeSampleUserProfile(profileCustomFields)
    ).then(mappedSample =>
      setPredictedContext(
        hasLegacyStepMapping ? mappedSample : omit(mappedSample, 'steps')
      )
    )
  }, [
    config,
    stepId,
    setPredictedContext,
    profileCustomFields,
    hasLegacyStepMapping
  ])

  return [predictedContext, {hasLegacyStepMapping}]
}
